import React from 'react';
import styled from '@emotion/styled';
import AccordionWrapper from 'App/components/AccordionWrapper/AccordionWrapper';
import RxFillNumberSideBar from 'App/components/PatientPrescription/RxFillNumberSideBar';
import { dateOnlySlashesFormat, formatUtcDate } from 'App/services/DateService';
import PropTypes from 'prop-types';
import { contentMonographRoute } from 'App/common/routeLookup';
import { Link, generatePath } from 'react-router-dom';
import { camelCase } from 'lodash';
import { PatientCardLabel } from 'App/components/Assessments/sidebar/styles/PatientCard';

const LeftGutterStyle = styled.div`
    padding-left: 1.5em;
`;

// Custom header styled component to keep accordion header compact
const MedicationAccordionHeader = styled.div`
    cursor: pointer;
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #666;
    margin-bottom: 10px;

    .caret-icon {
        margin-right: 8px;
        transition: transform 0.2s ease;
        font-size: 12px;

        &.expanded {
            transform: rotate(90deg);
        }
    }

    span {
        margin: 0;
        user-select: none;
    }

    &:hover {
        opacity: 0.8;
    }
`;

// Header content component compatible with AccordionWrapper
const CustomHeaderContent = ({ onClick, title, isVisible, setIsVisible, className }) => (
    <>
        <MedicationAccordionHeader
            className={className}
            onClick={() => {
                setIsVisible(!isVisible);
                onClick?.(isVisible);
            }}
        >
            <i className={`fa fa-caret-right caret-icon ${isVisible ? 'expanded' : ''}`} />
            <span>{title}</span>
        </MedicationAccordionHeader>
        <Link
            id={`resource-link-${title}`}
            to={generatePath(contentMonographRoute, {
                label: camelCase(title),
            })}
            target="_blank"
        >
            <i className="fa-solid fa-up-right-from-square" />
        </Link>
    </>
);

CustomHeaderContent.propTypes = {
    onClick: PropTypes.func,
    title: PropTypes.string.isRequired,
    isVisible: PropTypes.bool.isRequired,
    setIsVisible: PropTypes.func.isRequired,
    className: PropTypes.string,
};

CustomHeaderContent.defaultProps = {
    onClick: null,
    className: '',
};

const SidebarMedicationAccordion = ({ medList, patientId }) => {
    if (!medList || medList.length === 0) return null;

    const title = medList[0]?.medication?.name || '--';
    const stateKey = `sidebarMedicationList-toggle-${medList[0]?.medication?.id}`;

    return (
        <AccordionWrapper title={title} stateKey={stateKey} HeaderContent={CustomHeaderContent}>
            {medList.map((patientMedication) => (
                <LeftGutterStyle
                    key={patientMedication.id}
                    id={`patient-medication-${patientMedication.medication.id}-info`}
                >
                    {patientMedication?.status?.[0]?.start && (
                        <div>
                            <PatientCardLabel>Medication Start </PatientCardLabel>
                            {formatUtcDate({
                                date: patientMedication.status[0].start,
                                format: dateOnlySlashesFormat,
                            })}
                        </div>
                    )}
                    <RxFillNumberSideBar patientId={patientId} medication={patientMedication.medication} />
                </LeftGutterStyle>
            ))}
        </AccordionWrapper>
    );
};

SidebarMedicationAccordion.propTypes = {
    medList: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            medication: PropTypes.shape({
                id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                name: PropTypes.string,
            }).isRequired,
            status: PropTypes.arrayOf(
                PropTypes.shape({
                    start: PropTypes.string,
                })
            ),
        })
    ).isRequired,
    patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default SidebarMedicationAccordion;
